import { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { LogoutButton } from '../components/LogoutButton';
import { NotificationsBell } from '../components/NotificationsBell';
import { ModalShell, ModalHeader } from '../components/server/ServerModals';
import './TransferCredits.css';
import {
  ArrowRightLeft, Wallet, User, DollarSign, FileText,
  Send, Check, AlertCircle, Zap, Info,
} from 'lucide-react';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary';

export function TransferCredits() {
  const { state, fetchDashboardData } = useApp();

  const [discordUserId, setDiscordUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [concept, setConcept] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [amountError, setAmountError] = useState('');
  const [success, setSuccess] = useState<{ amount: number; recipient: string } | null>(null);

  useEffect(() => {
    if (!state.user) fetchDashboardData();
  }, []);

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^(\d*\.?\d{0,2})$/;
    if (regex.test(value) || value === '') {
      setAmount(value);
      setAmountError('');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setAmountError('');

    const numAmount = parseFloat(amount);
    if (!discordUserId.trim()) {
      setError('El ID de usuario de Discord es requerido');
      return;
    }
    if (!numAmount || numAmount <= 0) {
      setAmountError('El monto debe ser mayor a 0');
      return;
    }
    if (numAmount > state.balance) {
      setAmountError(`Saldo insuficiente. Tienes $${state.balance.toFixed(2)} USD`);
      return;
    }
    if (!concept.trim()) {
      setError('El concepto es requerido');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/transfers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discordUserId: discordUserId.trim(),
          amount: numAmount,
          concept: concept.trim(),
        }),
        credentials: 'include',
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSuccess({ amount: numAmount, recipient: discordUserId.trim() });
        // Reset form
        setDiscordUserId('');
        setAmount('');
        setConcept('');
        // Refetch balance to reflect the deduction
        fetchDashboardData();
      } else {
        setError(data.error || 'Error al procesar la transferencia');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="db-page">
      {/* TOPBAR */}
      <header className="sp-topbar">
        <div className="sp-topbar-left">
          <Link to="/dashboard" className="sp-topbar-brand">
            <Zap size={16} />
            <span>{APP_NAME}</span>
          </Link>
          <span className="sp-topbar-divider" aria-hidden="true" />
          <span className="sp-topbar-crumb">Transferir saldo</span>
        </div>
        <div className="sp-topbar-right">
          <NotificationsBell />
          <LogoutButton />
        </div>
      </header>

      <main className="db-main">
        {/* HERO */}
        <section className="transfer-hero">
          <div className="transfer-hero-text">
            <span className="transfer-hero-eyebrow">
              <ArrowRightLeft size={14} /> Transferencia entre usuarios
            </span>
            <h1 className="transfer-hero-title">Envía saldo a otro usuario</h1>
            <p className="transfer-hero-sub">
              Transfiere créditos a cualquier usuario de Voltary por su ID de Discord.
              El saldo se descuenta de tu cuenta al instante.
            </p>
          </div>
        </section>

        {/* GRID */}
        <div className="sp-grid">
          {/* FORM */}
          <section className="sp-panel sp-col-8 sp-col-sm-12">
            <div className="sp-panel-header">
              <Send className="sp-panel-icon" />
              <h2 className="sp-panel-title">Datos de la transferencia</h2>
            </div>
            <div className="sp-panel-body">
              {error && (
                <div className="sp-banner sp-banner--error" style={{ borderRadius: 4, borderBottom: 'none', marginBottom: '1rem' }} role="alert">
                  <span className="sp-banner-icon"><AlertCircle size={16} /></span>
                  <span className="sp-banner-text">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="transfer-form">
                <div className="sp-form-field">
                  <label htmlFor="discordUserId">
                    <User size={12} /> ID de Usuario de Discord
                  </label>
                  <input
                    id="discordUserId"
                    type="text"
                    value={discordUserId}
                    onChange={(e) => {
                      setDiscordUserId(e.target.value.replace(/\D/g, '').slice(0, 20));
                      setError('');
                    }}
                    placeholder="123456789012345678"
                    className="sp-input"
                    style={{ fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.05em' }}
                    required
                  />
                  <p className="sp-form-hint">
                    <Info size={11} /> Activa el modo desarrollador en Discord (Ajustes → Avanzado) y copia el ID con click derecho sobre el usuario.
                  </p>
                </div>

                <div className="sp-form-field">
                  <label htmlFor="amount">
                    <DollarSign size={12} /> Monto a transferir
                  </label>
                  <div className={`transfer-amount ${amountError ? 'has-error' : ''} ${parseFloat(amount) > 0 ? 'is-valid' : ''}`}>
                    <span className="transfer-amount-currency">USD</span>
                    <input
                      id="amount"
                      type="text"
                      inputMode="decimal"
                      value={amount}
                      onChange={handleAmountChange}
                      placeholder="0.00"
                      className="transfer-amount-field"
                      autoFocus
                    />
                  </div>
                  {amountError && (
                    <p className="sp-form-hint sp-form-hint--error">
                      <AlertCircle size={11} /> {amountError}
                    </p>
                  )}
                </div>

                <div className="sp-form-field">
                  <label htmlFor="concept">
                    <FileText size={12} /> Concepto
                  </label>
                  <input
                    id="concept"
                    type="text"
                    value={concept}
                    onChange={(e) => setConcept(e.target.value.slice(0, 100))}
                    placeholder="Pago por servicios, regalo, etc."
                    maxLength={100}
                    className="sp-input"
                    required
                  />
                  <p className="sp-form-hint">{concept.length}/100 caracteres</p>
                </div>

                <div className="transfer-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setDiscordUserId('');
                      setAmount('');
                      setConcept('');
                      setError('');
                      setAmountError('');
                    }}
                    disabled={loading}
                  >
                    Limpiar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !discordUserId.trim() || !amount || !concept.trim()}
                  >
                    {loading ? (
                      <><span className="spinner-small" /> Procesando…</>
                    ) : (
                      <><Send size={14} /> Transferir {amount && parseFloat(amount) > 0 ? `$${parseFloat(amount).toFixed(2)}` : 'saldo'}</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </section>

          {/* SIDEBAR INFO */}
          <section className="sp-panel sp-col-4 sp-col-sm-12">
            <div className="sp-panel-header">
              <Wallet className="sp-panel-icon" />
              <h2 className="sp-panel-title">Tu saldo</h2>
            </div>
            <div className="sp-panel-body">
              <div className="transfer-balance">
                <span className="transfer-balance-label">Disponible ahora</span>
                <div className="transfer-balance-amount">
                  <span className="transfer-balance-currency">$</span>
                  {state.balance.toFixed(2).replace('.', ',')}
                </div>
                <span className="transfer-balance-suffix">USD</span>
              </div>

              <div className="transfer-info-divider" />

              <div className="transfer-tips">
                <h4 className="transfer-tips-title">
                  <Info size={12} /> Consejos
                </h4>
                <ul className="transfer-tips-list">
                  <li>Verifica el ID antes de transferir — las transferencias no se pueden revertir.</li>
                  <li>El destinatario recibirá una notificación en Discord.</li>
                  <li>Las transferencias entre cuentas son instantáneas.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* SUCCESS MODAL */}
      {success && (
        <ModalShell onClose={() => setSuccess(null)} size="sm">
          <div className="sp-modal-body" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
            <div className="success-icon-circle" style={{ background: 'rgba(34, 197, 94, 0.12)', color: 'var(--success, #22c55e)' }}>
              <Check size={32} />
            </div>
            <h2 className="sp-modal-title" style={{ marginTop: '1rem' }}>¡Transferencia exitosa!</h2>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: '0.5rem' }}>
              Has transferido <strong>${success.amount.toFixed(2)} USD</strong> al usuario{' '}
              <code style={{ background: 'var(--bg-elevated, var(--bg-card-hover))', padding: '0.125rem 0.375rem', borderRadius: '4px', fontSize: '0.85em' }}>
                {success.recipient}
              </code>
            </p>
          </div>
          <div className="sp-modal-footer">
            <button type="button" className="btn btn-primary" onClick={() => setSuccess(null)}>
              <Check size={14} /> Listo
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
